/**
 * Round 129b (owner ruling R-A1, 2026-10-22: "Word files shouldn't be included"): a problem report accepts images, PDF and plain text, each under 10 MB -
 * exactly what storage.rules already enforces for problemReports/**. The type and size are checked HERE, before any upload, so the learner is told at
 * once what was not attached and why (instead of a silent Storage refusal). Storage rules are unchanged: they remain the authority.
 */
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
/** the file picker's `accept` attribute (a hint; the check below is the rule) */
export const ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/bmp,image/heic,image/heif,image/avif,application/pdf,.pdf,text/plain,.txt';
/** Round 147 (security audit L3): raster image types only - an SVG can carry script, so it is refused here and in storage.rules. */
export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp', 'image/heic', 'image/heif', 'image/avif'] as const;

export type RefusalReason = 'type' | 'size' | 'count' | 'upload_failed';
export interface RefusedFile { name: string; reason: RefusalReason }

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|heic|heif|avif)$/i;

/** The content type to upload with: the browser's own when it knows it, else inferred from the extension (storage.rules checks the content type). */
export function attachmentContentType(file: { name: string; type: string }): string | null {
  const type = (file.type || '').toLowerCase();
  if ((ALLOWED_IMAGE_TYPES as readonly string[]).includes(type) || type === 'application/pdf' || type === 'text/plain') return type;
  if (type) return null;                                   // a known type that is not allowed (Word, zip, audio ...)
  const n = file.name.toLowerCase();
  if (n.endsWith('.pdf')) return 'application/pdf';
  if (n.endsWith('.txt')) return 'text/plain';
  if (IMAGE_EXT.test(n)) return `image/${n.endsWith('.jpg') || n.endsWith('.jpeg') ? 'jpeg' : n.split('.').pop()}`;
  return null;
}

export function checkAttachments(files: ReadonlyArray<{ name: string; type: string; size: number }>): { accepted: number[]; refused: RefusedFile[] } {
  const accepted: number[] = [];
  const refused: RefusedFile[] = [];
  files.forEach((f, i) => {
    if (attachmentContentType(f) === null) refused.push({ name: f.name, reason: 'type' });
    else if (f.size > MAX_ATTACHMENT_BYTES) refused.push({ name: f.name, reason: 'size' });
    else if (accepted.length >= MAX_ATTACHMENTS) refused.push({ name: f.name, reason: 'count' });
    else accepted.push(i);
  });
  return { accepted, refused };
}
